import { useMediaQuery } from '@mui/material'
import cx from 'classnames'
import React, { useContext, useRef } from 'react'

import { COUNTRY_SELECT_INPUT_VALUES } from '@js/common/constants'
import { useEvent } from '@js/common/hooks/useEvent'
import { OptionItem } from '@js/common/types/OptionItem'
import { getStepValue } from '@js/common/utils'
import { validationMessage } from '@js/common/validation'

import {
  QuizContext,
  QuizContextType
} from '@f/applications/QuizApp/context/QuizContext'
import QuestionHeading from '@f/applications/QuizApp/partials/QuestionHeading'
import {
  mapGooglePrediction,
  renderGooglePrediction
} from '@f/components/LocationFinder/LocationFinderBackup'
import { Form } from '@f/elements/HookForm'
import yup from '@f/elements/HookForm/validator/yup'

import { StreetAutocomplete } from '../../partials/StreetAutocomplete'
import { QuizStep } from '../../partials/types'
import { composeStreetName } from '../../partials/utils'

interface AddressProps {
  step: QuizStep
}

export interface AddressSchema {
  street: string
  postal_code: string
  city: string
  country: string
  streetNumber: string
}

const addressValidationSchema: yup.ObjectSchema<AddressSchema> = yup
  .object()
  .shape({
    street: yup.string().streetWithoutInappropriateWords(),
    postal_code: yup.string().postalCode(),
    city: yup.string().city(),
    country: yup.string().required(),
    streetNumber: yup.string().required(validationMessage.streetNumber.required)
  })

const QuestionObjectAddress = ({ step }: AddressProps) => {
  const context = useContext(QuizContext) as QuizContextType
  const isMobileView = useMediaQuery('(max-width: 901px)')
  const formRef = useRef<HTMLFormElement>()

  const handleFormSubmit = (values: AddressSchema) => {
    const { streetNumber, street, ...restValues } = values

    const submitValues = {
      street: composeStreetName(street, streetNumber),
      ...restValues
    }

    context.actions.updateValues({
      stepID: step.id,
      valuesToSave: Object.entries(submitValues).map(([name, value]) => ({
        name: `property_${name}`,
        value
      }))
    })

    context.actions.move({ to: 'next' })
  }

  const STREET_NUMBER_REGEX = /\s\d+\S*$/

  const defaultValues = useRef({
    street: getStepValue(step, 'property_street')?.replace(
      STREET_NUMBER_REGEX,
      ''
    ),
    postal_code: getStepValue(step, 'property_postal_code'),
    city: getStepValue(step, 'property_city'),
    country: getStepValue(step, 'property_country') || 'DE',
    streetNumber:
      getStepValue(step, 'property_street')?.match(STREET_NUMBER_REGEX)?.[0] ||
      ''
  })

  useEvent('NEXT_QUESTION_BUTTON_CLICKED', () => {
    formRef?.current?.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true })
    )
  })

  return (
    <>
      <QuestionHeading heading={step.heading} />
      <Form.Wrapper<AddressSchema>
        ref={formRef}
        validationSchema={addressValidationSchema}
        defaultValues={defaultValues.current}
        onSubmit={handleFormSubmit}
        mode='all'
        wtEnabled={false}
        className={cx(
          '[ f-m-t-020 ] QuestionObjectAddress',
          !isMobileView && 'f-m-b-080'
        )}
        data-wtloc={`question-${step._wtName}`}>
        <div className='f-d-flr'>
          <div className={'f-fl-dbl f-m-r-020 f-m-b-020'}>
            <StreetAutocomplete>
              {({ suggestions, onSuggestionSelect }) => (
                <Form.Combobox
                  onItemSelect={onSuggestionSelect}
                  items={suggestions.map(mapGooglePrediction)}
                  name='street'
                  renderSuggestion={renderGooglePrediction}
                  className='[ f-c-bo-neu050 ] has-border'
                  placeholder='Straße'
                  inputProps={{
                    'data-testid': `combobox-street`,
                    'data-sel': 'ipt-strt',
                    autoComplete: 'street-address',
                    id: 'streetName'
                  }}
                />
              )}
            </StreetAutocomplete>
          </div>
          <div className='f-fl-default'>
            <Form.Text
              className='[ f-c-bo-neu050 ] has-border is-full-width'
              name='streetNumber'
              placeholder='Nummer'
              inputProps={{
                'data-testid': `input-streetNumber`,
                'data-sel': 'ipt-sn',
                id: 'streetNumber'
              }}
            />
          </div>
        </div>
        <div className='f-d-flr'>
          <div className='f-m-r-020 f-fl-default'>
            <Form.Text
              className='[ f-c-bo-neu050 ] has-border is-full-width'
              name='postal_code'
              placeholder='PLZ'
              inputProps={{
                'data-sel': 'ipt-zip',
                autoComplete: 'postal-code',
                id: 'zip'
              }}
            />
          </div>

          <div className={isMobileView ? 'f-fl-default' : 'f-fl-dbl'}>
            <Form.Text
              className='[ f-c-bo-neu050 ] has-border is-full-width'
              name='city'
              placeholder='Stadt'
              inputProps={{
                'data-sel': 'ipt-city',
                id: 'city',
                autoComplete: 'address-level2'
              }}
            />
          </div>
        </div>
        <Form.Select
          className='[ f-c-ba-whi f-c-fo-pri f-c-bo-neu050 ] has-border is-full-width'
          name='country'
          options={
            (COUNTRY_SELECT_INPUT_VALUES as unknown) as OptionItem<string>[]
          }
          inputProps={{ 'data-sel': 'slct-ctry' }}
        />
      </Form.Wrapper>
    </>
  )
}
export default QuestionObjectAddress
